import { useNavigate } from "react-router"
import { useTranslation } from "react-i18next"
import { ClockIcon, OctagonXIcon, SmartphoneIcon, WalletIcon } from "lucide-react"

import type { Overview, Topup } from "@/api/generated/model"
import { MoneyDisplay } from "@/components/kit/MoneyDisplay"
import { StatCard } from "@/components/kit/StatCard"
import { useAlertSources } from "@/app/layouts/alerts/use-alert-sources"
import { useConfig } from "@/auth/mode"
import { formatDate } from "@/lib/datetime"
import { isOffline } from "@/lib/devices"
import { formatCount, localeOf } from "@/lib/format"
import { useFirstDevices, useOverviewAccess, useTopups } from "../api/overview"

const DASH = "—"

/**
 * FR-DSH-OVW-01: what needs doing, each card linked to the list it counts
 * and present only with its own permission (money only in normal mode).
 * Failed-in-24-h is the bell's own count (LLD §7.6).
 */
export function ActionCards({ overview }: { overview: Overview | undefined }) {
  const { t, i18n } = useTranslation("overview")
  const locale = localeOf(i18n.language)
  const navigate = useNavigate()
  const can = useOverviewAccess()
  const failed = useAlertSources().find((a) => a.id === "failedMessages")
  const devices = useFirstDevices(can.devices)
  const topups = useTopups(can.wallet)

  const capped = (n: number, more: boolean | undefined) =>
    `${formatCount(n, locale)}${more ? "+" : ""}`
  const offline = devices.data
    ? capped(devices.data.items.filter(isOffline).length, devices.data.hasMore)
    : DASH
  const topupItems: Topup[] | undefined = topups.data?.items
  const pending = topupItems
    ? capped(topupItems.filter((x) => x.status === "pending").length, false)
    : DASH
  const wallet = overview?.wallet
  // DW-100: below the settings' threshold the balance is a warning.
  const threshold = useConfig().data?.topup?.lowBalanceThreshold
  const low = wallet !== undefined && threshold !== undefined && wallet.balance < threshold

  return (
    <section aria-label={t("actions.label")} className="flex flex-wrap gap-3">
      {can.devices ? (
        <StatCard
          label={t("actions.offlineDevices")}
          value={offline}
          hint={t("actions.offlineDevicesHint")}
          icon={SmartphoneIcon}
          tone="warning"
          onClick={() => void navigate("/devices")}
        />
      ) : null}
      {failed ? (
        <StatCard
          label={t("actions.failed24h")}
          value={failed.count === undefined ? DASH : formatCount(failed.count, locale)}
          hint={t("actions.failed24hHint")}
          icon={OctagonXIcon}
          tone="danger"
          onClick={() => void navigate(failed.to)}
        />
      ) : null}
      {can.wallet ? (
        <StatCard
          label={t("actions.pendingTopups")}
          value={pending}
          hint={t("actions.pendingTopupsHint")}
          icon={ClockIcon}
          tone="info"
          onClick={() => void navigate("/wallet")}
        />
      ) : null}
      {can.wallet && wallet ? (
        <StatCard
          label={t("actions.wallet")}
          value={<MoneyDisplay amount={wallet.balance} />}
          hint={
            low
              ? t("actions.walletLow")
              : wallet.nextExpiry
                ? t("actions.walletExpiry", { date: formatDate(wallet.nextExpiry, locale) })
                : t("actions.walletNoExpiry")
          }
          icon={WalletIcon}
          tone={low ? "warning" : "accent"}
          onClick={() => void navigate("/wallet")}
        />
      ) : null}
    </section>
  )
}
